import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Chip,
  Button,
  Skeleton,
} from '@mui/material';
import {
  Refresh as ResendIcon,
  Cancel as RevokeIcon,
} from '@mui/icons-material';
import { PendingInviteDto } from '../../api/userManagementApi';

interface PendingInvitesListProps {
  invites: PendingInviteDto[];
  isLoading: boolean;
  onResend: (invite: PendingInviteDto) => void;
  onRevoke: (invite: PendingInviteDto) => void;
  isResending: boolean;
  isRevoking: boolean;
}

const formatTimeRemaining = (expiresAt: string): string => {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffMs = expiry.getTime() - now.getTime();

  if (diffMs <= 0) {
    return 'Expired';
  }

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return `Expires in ${diffDays} day${diffDays > 1 ? 's' : ''}`;
  }

  if (diffHours > 0) {
    return `Expires in ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
  }

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  return `Expires in ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const PendingInvitesList: React.FC<PendingInvitesListProps> = ({
  invites,
  isLoading,
  onResend,
  onRevoke,
  isResending,
  isRevoking,
}) => {
  if (isLoading) {
    return (
      <Stack spacing={2}>
        {[1, 2, 3].map(i => (
          <Card key={i} variant="outlined">
            <CardContent>
              <Stack spacing={1.5}>
                <Skeleton width="70%" height={24} />
                <Skeleton width="50%" height={20} />
                <Skeleton width="60%" height={20} />
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  }

  if (invites.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No pending invitations
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {invites.map(invite => {
        const isExpired = invite.isExpired;
        const canRevoke = !isExpired;

        return (
          <Card
            key={invite.invitationId}
            variant="outlined"
            data-testid={`invite-card-${invite.invitationId}`}
          >
            <CardContent>
              <Stack spacing={2}>
                {/* Header: Email and Status */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <Typography variant="body1" fontWeight={500}>
                    {invite.invitedEmail}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Chip
                      label={
                        isExpired
                          ? 'Expired'
                          : formatTimeRemaining(invite.expiresAt)
                      }
                      size="small"
                      color={isExpired ? 'error' : 'default'}
                      variant={isExpired ? 'filled' : 'outlined'}
                    />
                    {invite.hasBeenAccessed && !isExpired && (
                      <Chip
                        label="Accessed"
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>

                {/* Details */}
                <Stack spacing={1}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Invited by
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {invite.invitedByUserName}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Sent on
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formatDate(invite.createdAt)}
                    </Typography>
                  </Box>
                </Stack>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 1, pt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ResendIcon />}
                    onClick={() => onResend(invite)}
                    disabled={isResending || isRevoking}
                    data-testid={`resend-invite-${invite.invitationId}`}
                  >
                    Resend
                  </Button>
                  {canRevoke && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<RevokeIcon />}
                      onClick={() => onRevoke(invite)}
                      disabled={isResending || isRevoking}
                      data-testid={`revoke-invite-${invite.invitationId}`}
                    >
                      Revoke
                    </Button>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
};
